import type { RouteRecordRaw } from 'vue-router';
import AdminLayout from '@/layouts/AdminLayout.vue';

/**
 * Admin Routes
 *
 * Routes for the admin dashboard with protected access.
 * All routes use the AdminLayout wrapper.
 */

export const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    component: AdminLayout,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
    },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard',
      },
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/DashboardView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'draws',
        name: 'admin-draws',
        component: () => import('@/views/admin/DrawsView.vue'),
        meta: { title: 'Draws' },
      },
      {
        path: 'draws/create',
        name: 'admin-draws-create',
        component: () => import('@/views/admin/CreateDrawView.vue'),
        meta: { title: 'Create Draw' },
      },
      {
        path: 'revenue',
        name: 'admin-revenue',
        component: () => import('@/views/admin/RevenueView.vue'),
        meta: { title: 'Revenue' },
      },
      {
        path: 'fans',
        name: 'admin-fans',
        component: () => import('@/views/admin/UsersListView.vue'),
        meta: { title: 'Fans' },
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/UsersListView.vue'),
        meta: { title: 'Users' },
      },
      {
        path: 'users/:id',
        name: 'admin-user-detail',
        component: () => import('@/views/admin/UserDetailView.vue'),
        meta: { title: 'User Details' },
      },
      {
        path: 'users/:id/edit',
        name: 'admin-user-edit',
        component: () => import('@/views/admin/UserDetailView.vue'),
        meta: { title: 'Edit User' },
      },
      {
        path: 'users/new',
        name: 'admin-user-create',
        component: () => import('@/views/admin/UserDetailView.vue'),
        meta: { title: 'Create User' },
      },
      {
        path: 'fixtures',
        name: 'admin-fixtures',
        component: () => import('@/views/admin/FixturesManagementView.vue'),
        meta: { title: 'Fixtures' },
      },
      {
        path: 'games/setup',
        name: 'admin-game-setup',
        component: () => import('@/views/admin/GameSetupWizardView.vue'),
        meta: { title: 'New Game Night' },
      },
      {
        path: 'games/:id/setup',
        name: 'admin-game-edit',
        component: () => import('@/views/admin/GameSetupWizardView.vue'),
        meta: { title: 'Edit Game' },
      },
      {
        path: 'clubs/new',
        name: 'admin-club-new',
        component: () => import('@/views/admin/ClubOnboardingView.vue'),
        meta: { title: 'New Club' },
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: () => import('@/views/admin/SettingsView.vue'),
        meta: { title: 'Settings' },
      },
    ],
  },
];

/**
 * Navigation Guard for Admin Routes
 *
 * Add this to your router configuration:
 *
 * router.beforeEach((to, from, next) => {
 *   const authStore = useAuthStore();
 *
 *   // Check if route requires authentication
 *   if (to.meta.requiresAuth && !authStore.isAuthenticated) {
 *     next({ name: 'login', query: { redirect: to.fullPath } });
 *     return;
 *   }
 *
 *   // Check if route requires admin access
 *   if (to.meta.requiresAdmin && !authStore.user?.isAdmin) {
 *     next({ name: 'home' });
 *     return;
 *   }
 *
 *   next();
 * });
 */
